// Сравнение «последний / предыдущий / первый»: значения, разницы с цветом, темп в неделю,
// индекс рекомпозиции и предупреждение о разных условиях замера.

import { Card } from '@/components/ui'
import { formatDate, formatNumber, formatSigned } from '@/lib/format'

import type { ComparisonRow, InBodySummaryOut, Recomposition } from '../api'
import { FIELD_LABELS } from '../labels'
import { Delta } from './delta'
import { isGood, toneClass } from './deltaFormat'

function Value({ row, value }: { row: ComparisonRow; value: number | null }) {
  return <span className="tabular-nums">{formatNumber(value, { digits: FIELD_LABELS[row.field].digits })}</span>
}

function Change({ row, delta, rate }: { row: ComparisonRow; delta: number | null; rate: number | null }) {
  return (
    <div className="flex flex-col items-end">
      <Delta delta={delta} row={row} />
      {rate !== null && delta !== null ? (
        <span className={`text-xs tabular-nums ${toneClass(isGood(rate, row.better))}`}>
          {`${formatSigned(rate, { digits: 2 })} ${row.unit ?? 'ед.'}/нед`}
        </span>
      ) : null}
    </div>
  )
}

function RecompositionLine({ title, value }: { title: string; value: Recomposition | null }) {
  if (!value) return null
  const tone = value.index_kg > 0 ? 'text-accent' : value.index_kg < 0 ? 'text-danger' : 'text-fg'
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
      <span className="text-muted">{title}</span>
      <span className={`font-semibold tabular-nums ${tone}`}>{formatSigned(value.index_kg, { digits: 1 })} кг</span>
      <span className="text-xs tabular-nums text-muted">
        мышцы {formatSigned(value.muscle_delta_kg, { digits: 1 })}, жир {formatSigned(value.fat_delta_kg, { digits: 1 })}
      </span>
    </div>
  )
}

export function ComparisonCard({ summary }: { summary: InBodySummaryOut }) {
  const { latest, previous, first } = summary
  if (!latest) return null
  const rows = summary.comparison.filter((r) => r.last !== null)
  return (
    <Card title="Сравнение замеров" flush>
      {!previous ? (
        <p className="px-4 pb-4 text-sm text-muted">
          Пока один замер — нужен второй замер, и здесь появятся разницы и темп изменений.
        </p>
      ) : (
        <>
          {summary.conditions_differ ? (
            <p role="status" className="mx-4 mb-3 rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning">
              {summary.conditions_differ}
            </p>
          ) : null}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[36rem] text-sm">
              <thead>
                <tr className="border-b border-border text-xs text-muted">
                  <th className="px-4 py-2 text-left font-medium">Величина</th>
                  <th className="px-2 py-2 text-right font-medium">Последний {formatDate(latest.day)}</th>
                  <th className="px-2 py-2 text-right font-medium">Предыдущий {formatDate(previous.day)}</th>
                  <th className="px-2 py-2 text-right font-medium">Первый {first ? formatDate(first.day) : ''}</th>
                  <th className="px-2 py-2 text-right font-medium">к предыдущему</th>
                  <th className="px-4 py-2 text-right font-medium">к первому</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((row) => (
                  <tr key={row.field}>
                    <td className="px-4 py-2 text-fg">{row.label_ru}</td>
                    <td className="px-2 py-2 text-right font-semibold">
                      <Value row={row} value={row.last} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Value row={row} value={row.previous} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Value row={row} value={row.first} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Change row={row} delta={row.delta_previous} rate={row.rate_previous_per_week} />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Change row={row} delta={row.delta_first} rate={row.rate_first_per_week} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 grid gap-1.5 border-t border-border px-4 py-3">
            <h3 className="text-sm font-semibold text-fg">Индекс рекомпозиции</h3>
            <RecompositionLine title="к предыдущему" value={summary.recomposition_previous} />
            <RecompositionLine title="к первому" value={summary.recomposition_first} />
            <p className="text-xs text-muted">
              Индекс — прирост мышц минус прирост жира, кг. Плюс: мышцы прибавляют (или жир уходит) быстрее, чем
              наоборот; минус: жир растёт быстрее мышц.
            </p>
          </div>
        </>
      )}
    </Card>
  )
}
